import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import extraCostsApiParser, { type ExtraCostItem } from './extraCostsApiParser';

// ---------- Types ----------
export interface ExtraCost {
  id: string | number;
  srno: number;
  vessel: string;
  manager: string;
  port: string;
  date: string;
  supplier: string;
  lub_costs: string | number;
  ex_costs: string | number;
  ex_costs_fifo: number;
  type: string;
  invoiced: string;
  remarks: string;
}

export interface GetExtraCostsListParams {
  [key: string]: any; // refine based on actual request params
}

// ---------- API Slice (merged) ----------
const extraCostsApi = createApi({
  reducerPath: 'extraCostsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ExtraCost'],
  endpoints: (builder) => ({
    // Common ping URL to satisfy axiosBaseQuery; response is ignored by transformers
    // and replaced with local mock data until real endpoints are ready.

    // Grid list using axiosBaseQuery + transformResponse parser
    getExtraCostsList: builder.query<any, void>({
      query: () => {
        return {
          url: `/extracost/all`,
          method: 'GET',
        };
      },
      transformResponse: (res: any) => {
        return extraCostsApiParser.ExtraCostsListParser(res);
      },
      providesTags: ['ExtraCost'],
    }),

    // Mutations for extra cost CRUD (mock)
    createExtraCost: builder.mutation<ExtraCostItem, { name: string; allocationType: string }>({
      query: ({ name, allocationType }) => ({
        url: `/extracost`,
        method: 'POST',
        data: { name, allocationType },
      }),
      invalidatesTags: ['ExtraCost'],
    }),

    updateExtraCost: builder.mutation<ExtraCostItem, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/extracost/${id}`,
        method: 'PUT',
        data: updates,
      }),
      invalidatesTags: ['ExtraCost'],
    }),

    deleteExtraCost: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/extracost/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExtraCost'],
    }),

    // Get extra costs for dropdown options
    getExtraCostsOptions: builder.query<{ value: string; label: string }[], void>({
      query: () => ({
        url: `/extracost/all`,
        method: 'GET',
      }),
      transformResponse: (res: any): { value: string; label: string }[] => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        return content.map((item: any) => ({
          value: String(item.id),
          label: String(item.name || item.extraCostName || 'Unknown'),
        }));
      },
      providesTags: ['ExtraCost'],
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const extraCostsReducer = extraCostsApi.reducer;
export const extraCostsMiddleware = extraCostsApi.middleware;
export const extraCostsReducerPath = extraCostsApi.reducerPath;

// ---------- Hooks / Actions ----------
export const {
  useGetExtraCostsListQuery,
  useGetExtraCostsOptionsQuery,
  useCreateExtraCostMutation,
  useUpdateExtraCostMutation,
  useDeleteExtraCostMutation,
} = extraCostsApi;
